// src/components/common/RichTextRenderer.jsx
import React from "react";
import { Link as RouterLink } from "react-router-dom";

const renderTextNode = (node, index) => {
  let children = <>{node.text}</>;
  if (node.type === "link") {
    const isInternal = /^\/(?!\/)/.test(node.url);
    if (isInternal) {
      return (
        <RouterLink
          key={`link-${index}`}
          to={node.url}
          className="text-blue-600 hover:text-blue-800 underline"
        >
          {node.children.map(renderTextNode)}
        </RouterLink>
      );
    } else {
      return (
        <a
          key={`link-${index}`}
          href={node.url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 hover:text-blue-800 underline"
        >
          {node.children.map(renderTextNode)}
        </a>
      );
    }
  }
  if (node.bold) {
    children = <strong>{children}</strong>;
  }
  if (node.italic) {
    children = <em>{children}</em>;
  }
  if (node.underline) {
    children = <u>{children}</u>;
  }
  if (node.strikethrough) {
    children = <s>{children}</s>;
  }
  if (node.code) {
    children = <code>{children}</code>;
  }
  return <React.Fragment key={`text-${index}`}>{children}</React.Fragment>;
};

const RichTextRenderer = ({
  content,
  className = "prose prose-lg lg:prose-xl max-w-none text-gray-800",
}) => {
  if (!content || !Array.isArray(content)) {
    return null;
  }

  const renderBlock = (block, index) => {
    const children = block.children ? block.children.map(renderTextNode) : null;

    switch (block.type) {
      case "heading":
        switch (block.level) {
          case 1:
            return (
              <h1 key={index} className="mt-8 mb-4 text-4xl font-bold">
                {children}
              </h1>
            );
          case 2:
            return (
              <h2 key={index} className="mt-7 mb-3 text-3xl font-bold">
                {children}
              </h2>
            );
          case 3:
            return (
              <h3 key={index} className="mt-6 mb-3 text-2xl font-semibold">
                {children}
              </h3>
            );
          case 4:
            return (
              <h4 key={index} className="mt-5 mb-2 text-xl font-semibold">
                {children}
              </h4>
            );
          case 5:
            return (
              <h5 key={index} className="mt-4 mb-2 text-lg font-semibold">
                {children}
              </h5>
            );
          case 6:
            return (
              <h6 key={index} className="mt-3 mb-2 text-base font-semibold">
                {children}
              </h6>
            );
          default:
            return (
              <p key={index} className="my-4 leading-relaxed">
                {children}
              </p>
            );
        }
      case "paragraph":
        return (
          <p key={index} className="my-2 leading-none text-base">
            {children}
          </p>
        );

      case "list":
        const ListTag = block.format === "ordered" ? "ol" : "ul";
        return (
          <ListTag
            key={index}
            className={
              block.format === "ordered" ? "list-decimal" : "list-disc"
            }
          >
            {block.children.map((listItem, listItemIndex) => (
              <li key={listItemIndex}>
                {listItem.children &&
                listItem.children.length > 0 &&
                listItem.children[0].type === "paragraph"
                  ? listItem.children[0].children.map(renderTextNode)
                  : listItem.children.map(renderTextNode)}
              </li>
            ))}
          </ListTag>
        );

      case "image":
        if (block.image && block.image.url) {
          return (
            <figure key={index} className="my-6">
              <img
                src={`${block.image.url}`}
                alt={block.image.alternativeText || block.image.name || ""}
                className="max-w-full h-auto rounded-md shadow-md mx-auto"
              />
              {block.image.caption && (
                <figcaption className="text-center text-sm text-gray-600 mt-2">
                  {block.image.caption}
                </figcaption>
              )}
            </figure>
          );
        }
        return null;

      case "quote":
        return (
          <blockquote
            key={index}
            className="border-l-4 border-gray-300 pl-4 italic my-6"
          >
            {children}
          </blockquote>
        );

      case "link":
        if (block.url) {
          const isInternal = /^\/(?!\/)/.test(block.url);
          if (isInternal) {
            return (
              <RouterLink
                key={index}
                to={block.url}
                className="text-blue-600 hover:text-blue-800 underline"
              >
                {children}
              </RouterLink>
            );
          } else {
            return (
              <a
                key={index}
                href={block.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800 underline"
              >
                {children}
              </a>
            );
          }
        }
        return <span key={index}>{children}</span>;

      default:
        console.warn(
          "Unsupported block type in RichTextRenderer:",
          block.type,
          block
        );
        return null;
    }
  };

  return <div className={className}>{content.map(renderBlock)}</div>;
};

export default RichTextRenderer;
